
import { CustomError, ICustomErrorProps } from '@ms/utilities-error/lib/index';

export class GraphError extends CustomError {
    public readonly response: IErrorResponse;
    public readonly status: number;
    public readonly serverStack: string;
    public readonly error: Error;
    public readonly request: XMLHttpRequest;

    constructor(error: Error, params: IErrorParams) {
        const props = {
            innerError: error,
            ...params
        };

        super(props);

        /* tslint:disable:no-use-before-declare */
        const {
            response: {
                error: {
                    innerError: {
                        stackTrace = ''
                    } = {}
                } = {}
            } = {},
            response,
            request,
            status
        } = params;
        /* tslint:enable:no-use-before-declare */

        const {
            message
        } = error;

        this.name = 'GraphError';
        this.message = message;
        this.response = response;
        this.status = status;
        this.serverStack = stackTrace;
        this.request = request;
        this.error = error;
    }
}

export interface IErrorParams extends ICustomErrorProps {
    response?: IErrorResponse;
    status: number;
    request?: XMLHttpRequest;
}

export interface IErrorResponse {
    error?: IError;
}

export interface IError {
    code: ErrorCode | InnerErrorCode | ClientErrorCode;
    fixItUrl?: string;
    message?: string;
    /**
     * Inner error (lowercase) as returned by the ODC direct endpoint.
     */
    innererror?: IError;
    /**
     * Inner error (camelCase) as returned by the Graph API.
     */
    innerError?: IError;
    stackTrace?: string;
    debugMessage?: string;
    localizedMessage?: string;
}

export type ErrorCode =
    'accessDenied' |
    'activityLimitReached' |
    'generalException' |
    'invalidRange' |
    'invalidRequest' |
    'itemNotFound' |
    'malwareDetected' |
    'nameAlreadyExists' |
    'notAllowed' |
    'notSupported' |
    'resourceModified' |
    'resourceNotFound' |
    'resyncRequired' |
    'serviceNotAvailable' |
    'quotaLimitReached' |
    'unauthenticated';

export type InnerErrorCode =
    'accessRestricted' |
    'cannotSnapshotTree' |
    'childItemCountExceeded' |
    'entityTagDoesNotMatch' |
    'fragmentLengthMismatch' |
    'fragmentOutOfOrder' |
    'fragmentOverlap' |
    'hipCheckRequired' |
    'insufficientVaultQuota' |
    'invalidAcceptType' |
    'invalidParameterFormat' |
    'invalidPath' |
    'invalidStartIndex' |
    'lockMismatch' |
    'lockNotFoundOrAlreadyExpired' |
    'lockOwnerMismatch' |
    'malformedEntityTag' |
    'maxDocumentCountExceeded' |
    'maxFileSizeExceeded' |
    'maxFolderCountExceeded' |
    'maxFragmentLengthExceeded' |
    'maxItemCountExceeded' |
    'maxQueryLengthExceeded' |
    'maxStreamSizeExceeded' |
    'nameContainsInvalidCharacters' |
    'parameterIsTooLong' |
    'parameterIsTooSmall' |
    'pathIsTooLong' |
    'pathIsTooDeep' |
    'propertyNotUpdateable' |
    'resyncApplyDifferences' |
    'resyncUploadDifferences' |
    'serviceReadOnly' |
    'throttledRequest' |
    'tooManyResultsRequested' |
    'tooManyItemsInQuery' |
    'totalAffectedItemCountExceeded' |
    'truncationNotAllowed' |
    'unlockRequired' |
    'uploadSessionFailed' |
    'uploadSessionIncomplete' |
    'uploadSessionNotFound' |
    'versionNumberDoesNotMatch' |
    'virusSuspicious' |
    'zeroOrFewerResultsRequested' |
    'ownerCannotShareWithSelf';

export type ClientErrorCode =
    'timeout' |
    'authFailure';

const errorMessage = 'errorMessage';
const vroomStatus = 'vroomStatus';
const vroomErrorCode = 'vroomErrorCode';
const vroomErrorMessage = 'vroomErrorMessage';
const vroomErrorInnerCode = 'vroomErrorInnerCode';
const vroomErrorInnerMessage = 'vroomErrorInnerMessage';
const vroomErrorInnerDebugMessage = 'vroomErrorInnerDebugMessage';
const vroomErrorInnerStack = 'vroomErrorInnerStack';

export function getQosExtraDataFromError(error: Error): { [key: string]: string | number | boolean; } {
    const extraData: {
        [key: string]: string | number | boolean;
    } = {};

    extraData[errorMessage] = error.message;

    if (isGraphError(error)) {
        extraData[vroomStatus] = error.status;

        const {
            response
        } = error;

        if (response && response.error) {
            const responseError = response.error;

            extraData[vroomErrorCode] = responseError.code;
            extraData[vroomErrorMessage] = responseError.message;

            const innerError = responseError.innerError || responseError.innererror;

            if (innerError) {
                extraData[vroomErrorInnerCode] = innerError.code;
                extraData[vroomErrorInnerMessage] = innerError.message;
                extraData[vroomErrorInnerDebugMessage] = innerError.debugMessage;
                extraData[vroomErrorInnerStack] = innerError.stackTrace;
            }
        }
    }

    return extraData;
}

export function isGraphError(error: Error): error is GraphError {
    return error instanceof GraphError;
}

export type AggregateErrorCode = ErrorCode | InnerErrorCode | ClientErrorCode | number;

export function getErrorResolution<T>(error: Error, resolveError: (code: AggregateErrorCode, error?: GraphError) => T): T | undefined {
    if (isGraphError(error)) {
        const {
            response,
            status
        } = error;

        if (response) {
            const innerErrors: IError[] = [];

            let innerError = response.error;

            while (innerError) {
                innerErrors.push(innerError);

                innerError = innerError.innerError || innerError.innererror;
            }

            while (innerError = innerErrors.pop()) {
                if (typeof innerError.code !== 'undefined') {
                    const resolution = resolveError(innerError.code, error);

                    if (resolution !== undefined) {
                        return resolution;
                    }
                }
            }
        }

        return resolveError(status, error);
    }
}

export function getResultCodeFromError(error: Error): string {
    return getErrorResolution(error, (code: AggregateErrorCode) => `${code}`) || 'unhandled';
}
